import React, { useState, useEffect } from "react";
import { Play, ExternalLink, Mail, Linkedin } from "lucide-react";

const App = () => {
  const [selectedVideo, setSelectedVideo] = useState<any>(null);

  // Sample videos - replace with your actual Vimeo video IDs and info
  const videos = [
    {
      id: "1107255440",
      title: "AI-Powered Content Sizzle Reel",
      description: "Compilation of custom AI-generated content.",
      category: "AI Content",
      thumbnail: "/Thumbnail_Images/AI-ContentThumbnail-2.jpg",
      year: "2024-2025",
    },
    {
      id: "789123456",
      title: "Digital Content Sizzle Reel",
      description: "Compilation of digital platform content.",
      category: "Digital Content",
      thumbnail: "/Thumbnail_Images/DigitalContentThumb--TEMP.jpg",
      year: "2023",
    },
    {
      id: "1110965103",
      title: "Corporate Content Sizzle Reel",
      description: "Compilation of corporate content.",
      category: "Corporate Content",
      thumbnail: "/Thumbnail_Images/CorporateContentSizzle--THUMB.png",
      year: "2008-2024",
    },
    {
      id: "710342045",
      title: "Motion Graphics",
      description:
        "Compilation of branded opens, lower thirds and motion graphics for national television.",
      category: "Adobe AfterEffects",
      thumbnail: "/Thumbnail_Images/MauryGraphicsThumb.jpg",
      year: "2010-2022",
    },
  ];

  const categories = ["All", ...new Set(videos.map((video) => video.category))];
  const [selectedCategory, setSelectedCategory] = useState("All");

  const filteredVideos =
    selectedCategory === "All"
      ? videos
      : videos.filter((video: any) => video.category === selectedCategory);

  const openVideo = (video: any) => {
    setSelectedVideo(video);
    window.history.pushState({ videoId: video.id }, "", `#video-${video.id}`);
  };

  const closeVideo = () => {
    setSelectedVideo(null);
    if (window.location.hash) {
      window.history.back();
    }
  };

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
  };

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      setSelectedVideo(null);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-950">
      {/* Hero Section */}
      <header className="relative h-screen flex items-center justify-center text-center text-white overflow-hidden">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
          src="/hero-reel.mp4"
        />
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-6">
          <h1 className="text-6xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">
            Brian Thomas
          </h1>
          <p className="text-xl md:text-2xl mb-8 text-gray-300">
            AI-powered Video Content Creator
          </p>
          <p className="text-lg mb-12 max-w-2xl mx-auto text-gray-400">
            Your brand needs attention grabbing videos, graphics, images, logos
            and websites that use AI-powered creativity.
          </p>
          <div className="flex justify-center space-x-6">
            <a
              href="#portfolio"
              className="flex items-center space-x-2 border border-white/30 hover:bg-white/10 px-6 py-3 rounded-full transition-colors"
            >
              <Play size={20} />
              <span>View Content</span>
            </a>
            <a
              href="#about"
              className="flex items-center space-x-2 border border-white/30 hover:bg-white/10 px-6 py-3 rounded-full transition-colors"
            >
              <Play size={20} />
              <span>View Tools</span>
            </a>
            <a
              href="#bio"
              className="flex items-center space-x-2 border border-white/30 hover:bg-white/10 px-6 py-3 rounded-full transition-colors"
            >
              <Play size={20} />
              <span>View Bio</span>
            </a>
          </div>
        </div>
      </header>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-16 text-white">
            Featured Content
          </h2>
        </div>
        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => handleCategoryChange(category)}
              className={`px-6 py-2 rounded-full transition-all ${
                selectedCategory === category
                  ? "bg-blue-600 text-white"
                  : "bg-white/10 text-gray-300 hover:bg-white/20"
              }`}
            >
              {category}
            </button>
          ))}
        </div>
        {/* Video Grid */}
        <div
          className={`grid grid-cols-1 ${filteredVideos.length > 1 ? "md:grid-cols-2" : "max-w-md"} gap-8 mb-12 max-w-5xl mx-auto justify-items-center`}
        >
          {filteredVideos.map((video) => (
            <div
              key={video.id}
              className="group cursor-pointer bg-white/5 rounded-2xl overflow-hidden hover:bg-white/10 transition-all duration-300 hover:scale-105"
              onClick={() => openVideo(video)}
            >
              <div className="relative aspect-video overflow-hidden">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center group-hover:bg-white/30 transition-colors">
                    <Play className="text-white ml-1" size={24} />
                  </div>
                </div>
                <div className="absolute top-4 right-4 bg-blue-600 text-white px-3 py-1 rounded-full text-sm">
                  {video.year}
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xl font-semibold text-white group-hover:text-blue-400 transition-colors">
                    {video.title}
                  </h3>
                  <span className="text-sm text-blue-400 bg-blue-400/20 px-2 py-1 rounded">
                    {video.category}
                  </span>
                </div>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {video.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>
      {/* Additional Content Section */}
      <section id="additional" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl mb-12 font-bold text-center mb-12 text-white">
            Content Links
          </h2>

          {/* Digital Content Section */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-white mb-8">
              Digital Content
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <a
                href="https://www.youtube.com/watch?v=VGX8T_4fY_o"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/5 hover:bg-white/10 rounded-xl p-4 transition-colors text-center"
              >
                <span className="text-white font-semibold">
                  SonyMusic/NBCU Marketing
                </span>
                <div className="text-blue-400 mt-2">View on YouTube →</div>
              </a>
              <a
                href="https://vimeo.com/manage/videos/707804864"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/5 hover:bg-white/10 rounded-xl p-4 transition-colors text-center"
              >
                <span className="text-white font-semibold">
                  Karamo Show TikTok
                </span>
                <div className="text-blue-400 mt-2">View on TikTok →</div>
              </a>
              <a
                href="https://www.tiktok.com/@karamoshow"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/5 hover:bg-white/10 rounded-xl p-4 transition-colors text-center"
              >
                <span className="text-white font-semibold">
                  YouTube Original Series
                </span>
                <div className="text-blue-400 mt-2">View on YouTube →</div>
              </a>
            </div>
          </div>

          {/* Broadcast Section */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-white mb-8">
              Broadcast Content
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <a
                href="https://vimeo.com/740051380"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/5 hover:bg-white/10 rounded-xl p-4 transition-colors text-center"
              >
                <span className="text-white font-semibold">
                  HBO Documentary Films Pitch
                </span>
                <div className="text-blue-400 mt-2">View on Vimeo →</div>
              </a>
              <a
                href="https://vimeo.com/551248645?share=copy"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/5 hover:bg-white/10 rounded-xl p-4 transition-colors text-center"
              >
                <span className="text-white font-semibold">
                  True Crime Stories
                </span>
                <div className="text-blue-400 mt-2">View on Vimeo →</div>
              </a>
              <a
                href="https://vimeo.com/manage/videos/758039937"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/5 hover:bg-white/10 rounded-xl p-4 transition-colors text-center"
              >
                <span className="text-white font-semibold">
                  NBC National Television Promo
                </span>
                <div className="text-blue-400 mt-2">View on Vimeo →</div>
              </a>
            </div>
          </div>

          {/* Corporate Section */}
          <div className="mb-16">
            <h3 className="text-3xl font-bold text-white mb-8">
              Corporate Content
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <a
                href="https://vimeo.com/758023364"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/5 hover:bg-white/10 rounded-xl p-4 transition-colors text-center"
              >
                <span className="text-white font-semibold">
                  Lexis Nexis Training Series
                </span>
                <div className="text-blue-400 mt-2">View on Vimeo →</div>
              </a>
              <a
                href="https://vimeo.com/469030095"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/5 hover:bg-white/10 rounded-xl p-4 transition-colors text-center"
              >
                <span className="text-white font-semibold">
                  Non-Profit Fundraising
                </span>
                <div className="text-blue-400 mt-2">View on Vimeo →</div>
              </a>
              <a
                href="https://vimeo.com/manage/videos/86842569"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/5 hover:bg-white/10 rounded-xl p-4 transition-colors text-center"
              >
                <span className="text-white font-semibold">
                  Trade Conference Welcome Video
                </span>
                <div className="text-blue-400 mt-2">View on Vimeo →</div>
              </a>
            </div>
          </div>
          <p className="text-xl text-center mb-16 text-gray-400">
            More content available upon request
          </p>
        </div>
      </section>
      {/* About Section */}
      <section id="about" className="py-20 px-6 bg-black/30">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-8 text-white">AI Tools</h2>
          <p className="text-lg text-gray-300 leading-relaxed mb-8">
            Agentic AI, ChatGPT, Claude, Descript, Eleven Labs, Figma AI, Kling,
            Luma AI, Midjourney, Runway, Sora, Synthesia, Topaz, Veo, Vibe
            Coding, Weavy
          </p>
          <h2 className="text-4xl font-bold mb-8 text-white">Video Tools</h2>
          <p className="text-lg text-gray-300 leading-relaxed mb-8">
            Adobe Premiere, Adobe AfterEffects, Davinci Resolve, Avid Media Composer, 
            Sony FX-3, 3-Point Lighting, Audio Recording, OBS Studio, Riverside, Teleprompting
          </p>
          <h2 className="text-4xl font-bold mb-8 text-white">
            Website Coding Tools
          </h2>
          <p className="text-lg text-gray-300 leading-relaxed mb-8">
            CSS, DevTools, GitHub, HTML, JavaScript, React, Netlify, MongoDB,
            Next.JS, Node.JS, SQL, VS Code
          </p>
        </div>
      </section>
      {/* Bio Section */}
      <section id="bio" className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Side by side: Image + Bio */}
<div className="flex flex-col md:flex-row items-center justify-center gap-12 mb-20 mx-auto">
  <div className="w-full md:w-1/3 h-60 overflow-hidden rounded-2xl ring-1 ring-white/10">
              <img
                src="/Thumbnail_Images/BTeditpic.jpg"
                alt="Brian Thomas in the edit bay"
                className="w-full h-full object-cover"
                style={{ objectPosition: "50% center" }}
              />
            </div>

            {/* Bio Text */}
            <div className="w-full md:w-1/2">
              <p className="text-xl text-white font-semibold mb-6">
                Emmy-nominated 25-year member of the{" "}
                <span className="whitespace-nowrap">
                  Motion Picture Editors Guild
                </span>
              </p>
              <div className="w-3/4 h-px bg-gradient-to-r from-transparent via-gray-400 to-transparent opacity-40 mb-6"></div>
              <p className="text-md text-gray-300 leading-relaxed">
                I edit, produce, create motion graphics that hook viewers in the first
                three seconds and cut out everything that doesn't move the story
                forward. I've spent 25+ years taking raw footage, writing
                voiceover copy, and turning ideas into video content that grabs
                attention — and holds it. From e-commerce streaming platforms to national television to corporate, social media and everything in between.
              </p>
            </div>
          </div>

          {/* Staff and Freelance Clients - Side by Side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Staff Timeline */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 text-center">
                Fulltime Projects
              </h2>
              <div className="relative">
                {/* Vertical line */}
                <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-px bg-gradient-to-b from-transparent via-gray-400 to-transparent opacity-40"></div>

                {/* Timeline entries */}
                <div className="space-y-12">
                  {/* Entry 1 - Amazon Live (LEFT) */}
                  <div className="relative flex items-center">
                    <div className="w-1/2 pr-8 text-right">
                      <h3 className="text-xl md:text-2xl font-bold text-white">
                        Amazon Live
                      </h3>
                      <p className="text-blue-400 font-semibold mt-1 text-sm">
                        2026 – Present
                      </p>
                    </div>
                    <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 rounded-full bg-blue-500 ring-4 ring-slate-900"></div>
                    <div className="w-1/2 pl-8"></div>
                  </div>

                  {/* Entry 2 - Karamo Show (RIGHT) */}
                  <div className="relative flex items-center">
                    <div className="w-1/2 pr-8"></div>
                    <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 rounded-full bg-blue-500 ring-4 ring-slate-900"></div>
                    <div className="w-1/2 pl-8">
                      <h3 className="text-xl md:text-2xl font-bold text-white">
                        Karamo {" "}
                        <span className="text-sm text-gray-400 font-normal">
                          (NBC)
                        </span>
                      </h3>
                      <p className="text-blue-400 font-semibold mt-1 text-sm">
                        2022 – 2026
                      </p>
                    </div>
                  </div>

                  {/* Entry 3 - LexisNexis (LEFT) */}
                  <div className="relative flex items-center">
                    <div className="w-1/2 pr-8 text-right">
                      <h3 className="text-xl md:text-2xl font-bold text-white">
                        LexisNexis
                      </h3>
                      <p className="text-blue-400 font-semibold mt-1 text-sm">
                        2022
                      </p>
                    </div>
                    <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 rounded-full bg-blue-500 ring-4 ring-slate-900"></div>
                    <div className="w-1/2 pl-8"></div>
                  </div>

                  {/* Entry 4 - Maury Povich Show (RIGHT) */}
                  <div className="relative flex items-center">
                    <div className="w-1/2 pr-8"></div>
                    <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 rounded-full bg-blue-500 ring-4 ring-slate-900"></div>
                    <div className="w-1/2 pl-8">
                      <h3 className="text-xl md:text-2xl font-bold text-white">
                        Maury {" "}
                        <span className="text-sm text-gray-400 font-normal">
                          (NBC)
                        </span>
                      </h3>
                      <p className="text-blue-400 font-semibold mt-1 text-sm">
                        2001 – 2022
                      </p>
                    </div>
                  </div>

                  {/* Entry 5 - New York Mets (LEFT) */}
                  <div className="relative flex items-center">
                    <div className="w-1/2 pr-8 text-right">
                      <h3 className="text-xl md:text-2xl font-bold text-white">
                        New York Mets
                      </h3>
                      <p className="text-blue-400 font-semibold mt-1 text-sm">
                        2000 – 2007
                      </p>
                    </div>
                    <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 rounded-full bg-blue-500 ring-4 ring-slate-900"></div>
                    <div className="w-1/2 pl-8"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Freelance Clients */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 text-center">
                Freelance Projects
                <span className="text-lg md:text-xl font-normal block mt-1">(On-Set Live Production & Editing)</span>
              </h2>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white/5 hover:bg-white/10 rounded-lg p-3 transition-colors text-center">
                  <h3 className="text-base font-bold text-white">
                    Amazon Web Services
                  </h3>
                </div>
                <div className="bg-white/5 hover:bg-white/10 rounded-lg p-3 transition-colors text-center">
                  <h3 className="text-base font-bold text-white">Apollo Global Management</h3>
                </div>
                <div className="bg-white/5 hover:bg-white/10 rounded-lg p-3 transition-colors text-center">
                  <h3 className="text-base font-bold text-white">
                    EY Global Ltd.
                  </h3>
                </div>
                <div className="bg-white/5 hover:bg-white/10 rounded-lg p-3 transition-colors text-center">
                  <h3 className="text-base font-bold text-white">
                    Fiserv
                  </h3>
                </div>
                <div className="bg-white/5 hover:bg-white/10 rounded-lg p-3 transition-colors text-center">
                  <h3 className="text-base font-bold text-white">
                    Google Digital Studio
                  </h3>
                </div>
                <div className="bg-white/5 hover:bg-white/10 rounded-lg p-3 transition-colors text-center">
                  <h3 className="text-base font-bold text-white">
                    KKR
                  </h3>
                </div>
                <div className="bg-white/5 hover:bg-white/10 rounded-lg p-3 transition-colors text-center">
                  <h3 className="text-base font-bold text-white">9/11 Museum</h3>
                </div>
                <div className="bg-white/5 hover:bg-white/10 rounded-lg p-3 transition-colors text-center">
                  <h3 className="text-base font-bold text-white">Northwell Health</h3>
                </div>
                <div className="bg-white/5 hover:bg-white/10 rounded-lg p-3 transition-colors text-center">
                  <h3 className="text-base font-bold text-white">Prudential</h3>
                </div>
                <div className="bg-white/5 hover:bg-white/10 rounded-lg p-3 transition-colors text-center">
                  <h3 className="text-base font-bold text-white">
                    Regeneron
                  </h3>
                </div>
                <div className="bg-white/5 hover:bg-white/10 rounded-lg p-3 transition-colors text-center">
                  <h3 className="text-base font-bold text-white">Avidbeer</h3>
                </div>
                <div className="bg-white/5 hover:bg-white/10 rounded-lg p-3 transition-colors text-center">
                  <h3 className="text-base font-bold text-white">
                    JetNation
                  </h3>
                </div>
                <div className="bg-white/5 hover:bg-white/10 rounded-lg p-3 transition-colors text-center">
                  <h3 className="text-base font-bold text-white">Newsport</h3>
                </div>
                <div className="bg-white/5 hover:bg-white/10 rounded-lg p-3 transition-colors text-center">
                  <h3 className="text-base font-bold text-white">
                    MetroGuide
                  </h3>
                </div>
                <div className="bg-white/5 hover:bg-white/10 rounded-lg p-3 transition-colors text-center">
                  <h3 className="text-base font-bold text-white">
                    Inextv.com
                  </h3>
                </div>
                <div className="bg-white/5 hover:bg-white/10 rounded-lg p-3 transition-colors text-center">
                  <h3 className="text-base font-bold text-white">Brigham Hospital</h3>
                </div>
                <div className="bg-white/5 hover:bg-white/10 rounded-lg p-3 transition-colors text-center">
                  <h3 className="text-base font-bold text-white">Abraham Law</h3>
                </div>
                <div className="bg-white/5 hover:bg-white/10 rounded-lg p-3 transition-colors text-center">
                  <h3 className="text-base font-bold text-white">
                    New Mountain Capital
                  </h3>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-center space-x-6 mt-16">
            <a
              href="https://www.linkedin.com/in/brian-thomas-video/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-blue-400 transition-colors"
            >
              <Linkedin size={24} />
            </a>
            <button
              onClick={() => {
                navigator.clipboard.writeText("btcontentai@gmail.com");
                alert("Email copied to clipboard");
              }}
              className="text-gray-400 hover:text-blue-400 transition-colors bg-transparent border-none cursor-pointer"
            >
              <Mail size={24} />
            </button>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-6xl bg-black rounded-2xl overflow-hidden">
            <button
              onClick={closeVideo}
              className="absolute top-4 right-4 z-10 w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center text-white transition-colors"
            >
              X
            </button>
            <div className="aspect-video">
              <iframe
                src={`https://player.vimeo.com/video/${selectedVideo.id}?autoplay=1&title=0&byline=0&portrait=0`}
                width="100%"
                height="100%"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={`Video: ${selectedVideo.title}`}
                className="w-full h-full"
              ></iframe>
            </div>
            <div className="p-6 bg-neutral-900">
              <h3 className="text-2xl font-bold text-white mb-2">
                {selectedVideo.title}
              </h3>
              <p className="text-gray-300 mb-4">{selectedVideo.description}</p>
              <div className="flex items-center justify-between">
                <span className="text-blue-400 bg-blue-400/20 px-3 py-1 rounded-full text-sm">
                  {selectedVideo.category}
                </span>
                <a
                  href={`https://vimeo.com/${selectedVideo.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 text-gray-300 hover:text-white transition-colors"
                >
                  <span>View on Vimeo</span>
                  <ExternalLink size={16} />
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
